import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import usePageTitle from '../hooks/usePageTitle';
import { images, loungeEntries as mockLounge, booking } from '../mock';
import { useBooking } from '../context/BookingContext';
import { ArrowUpRight, Clock } from 'lucide-react';
import { getLounge } from '../api';

const Lounge = () => {
  usePageTitle('Unpacked: The Lounge');
  const { openBooking } = useBooking();
  const [entries, setEntries] = useState(mockLounge);

  const loadLounge = useCallback(() => {
    getLounge()
      .then((data) => { if (Array.isArray(data) && data.length) setEntries(data); })
      .catch(() => {});
  }, [setEntries]);

  useEffect(() => { loadLounge(); }, [loadLounge]);

  const [feature, ...rest] = entries;

  return (
    <main>
      <section className="relative pt-40 pb-24">
        <div className="absolute inset-0">
          <img src={images.lounge} alt="" className="w-full h-full object-cover object-[70%_center]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#241033_0%,rgba(36,16,51,0.93)_52%,rgba(67,38,94,0.45)_100%)]" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="eyebrow text-silver mb-5">Unpacked · The Lounge</div>
            <h1 className="font-display text-5xl md:text-6xl text-white font-medium leading-tight max-w-3xl mb-6">
              Reflections on clarity, leadership & the human experience of work.
            </h1>
            <p className="text-[#D9CEE6] font-light text-lg max-w-2xl">
              A quiet space to unpack what we carry — essays and reflections for leaders and professionals in motion.
            </p>
          </Reveal>
        </div>
      </section>

      {feature && (
        <section className="py-24 bg-[#FBFAF8]">
          <div className="max-w-[1280px] mx-auto px-6">
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-12 items-center bg-white rounded-sm overflow-hidden card-lift">
                <div className="h-full min-h-[360px]">
                  <img src={feature.image} alt={feature.title} className="w-full h-full object-cover min-h-[360px]" />
                </div>
                <div className="p-10 lg:pr-16">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="eyebrow text-[#7E5C9E]">{feature.category}</span>
                    <span className="text-[#B9A9CC]">·</span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#6A6570] tracking-luxe uppercase">
                      <Clock className="w-3 h-3" strokeWidth={1.5} /> {feature.readTime || feature.read}
                    </span>
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl text-[#241033] font-medium leading-tight mb-5">{feature.title}</h2>
                  <p className="text-[#4A4453] font-light text-lg leading-relaxed mb-8">{feature.excerpt}</p>
                  {feature.slug ? (
                    <Link to={`/lounge/${feature.slug}`} className="group inline-flex items-center gap-2 text-[#43265E] text-sm tracking-luxe uppercase border-b border-[#43265E]/40 pb-1 hover:border-[#43265E] transition-all">
                      Read Reflection
                      <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
                    </Link>
                  ) : (
                    <span className="text-sm text-[#9A8AAF] italic">Coming soon</span>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="pb-24 bg-[#FBFAF8]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {rest.map((entry, i) => {
              const CardInner = (
                <article className="card-lift bg-white rounded-sm overflow-hidden h-full flex flex-col cursor-pointer group">
                  <div className="h-56 overflow-hidden">
                    <img src={entry.image} alt={entry.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="eyebrow text-[#7E5C9E]">{entry.category}</span>
                      <span className="text-[#B9A9CC] text-xs">· {entry.readTime || entry.read}</span>
                    </div>
                    <h3 className="font-display text-2xl text-[#241033] leading-snug mb-3">{entry.title}</h3>
                    <p className="text-[#6A6570] font-light text-sm leading-relaxed flex-1">{entry.excerpt}</p>
                    <span className="text-xs text-[#9A8AAF] font-light mt-5 tracking-luxe uppercase">{entry.date}</span>
                  </div>
                </article>
              );
              return (
                <Reveal key={entry.id || entry.slug || entry.title} delay={i * 90}>
                  {entry.slug ? <Link to={`/lounge/${entry.slug}`}>{CardInner}</Link> : CardInner}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#43265E] relative overflow-hidden">
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#5B3A7A]/40 blur-3xl" />
        <div className="relative max-w-[800px] mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-white font-medium mb-6">Love Letter to Professionals</h2>
          <p className="text-[#D9CEE6] font-light text-lg mb-10">
            An emerging series on professional identity, purpose, and the lived experience of navigating growth and transition. TAG us in to be part of the conversation.
          </p>
          <button onClick={() => openBooking(booking.clarityUnpacked, 'Clarity Unpacked')} className="btn-ivory rounded-full px-10 py-4 text-sm tracking-luxe uppercase">
            Begin a Conversation
          </button>
        </div>
      </section>
    </main>
  );
};

export default Lounge;
